/**
 * ScoreManager - Tracks score, combos, multipliers, and high scores
 */
export default class ScoreManager {
    constructor() {
        this.score = 0;
        this.highScore = this.loadHighScore();
        this.comboCount = 0;
        this.comboTimer = 0;
        this.comboTimeout = 5; // seconds before combo resets
        this.multiplier = 1;
        this.maxMultiplier = 8;

        // Score values
        this.scoreValues = {
            coinCollected: 100,
            checkpointReached: 500,
            challengeCompleted: 2000,
            meteorSurvived: 150,
            speedBonus: 10,       // per second above speed threshold
            driftBonus: 50,
            nearMissBonus: 200,
        };

        // Tracking
        this.totalCoinsCollected = 0;
        this.totalCheckpoints = 0;
        this.totalChallengesCompleted = 0;
        this.sessionStartTime = Date.now();
        this.speedBonusAccumulator = 0;

        // Speed bonus thresholds
        this.speedBonusThreshold = 50; // m/s (~180 km/h)

        // Callbacks
        this.onScoreChange = null;
        this.onComboChange = null;
        this.onMultiplierChange = null;
        this.onHighScore = null;
    }

    update(deltaTime, bus) {
        // Update combo timer
        if (this.comboCount > 0) {
            this.comboTimer -= deltaTime;
            if (this.comboTimer <= 0) {
                this.resetCombo();
            }
        }

        // Speed bonus: accumulate points while going fast
        if (bus && bus.getSpeed && bus.getSpeed() > this.speedBonusThreshold) {
            this.speedBonusAccumulator += deltaTime;
            if (this.speedBonusAccumulator >= 1) {
                this.speedBonusAccumulator -= 1;
                this.addScore('speedBonus', false);
            }
        }
    }

    addScore(type, incrementCombo = true) {
        const baseValue = this.scoreValues[type] || 0;
        if (baseValue === 0) return 0;

        const points = baseValue * this.multiplier;
        this.score += points;

        if (incrementCombo) {
            this.incrementCombo();
        }

        // Track stats
        if (type === 'coinCollected') this.totalCoinsCollected++;
        if (type === 'checkpointReached') this.totalCheckpoints++;
        if (type === 'challengeCompleted') this.totalChallengesCompleted++;

        // Check high score
        if (this.score > this.highScore) {
            this.highScore = this.score;
            this.saveHighScore();
            if (this.onHighScore) this.onHighScore(this.highScore);
        }

        if (this.onScoreChange) this.onScoreChange(this.score, points, type);

        return points;
    }

    addCustomScore(points, reason = '') {
        const finalPoints = points * this.multiplier;
        this.score += finalPoints;

        if (this.score > this.highScore) {
            this.highScore = this.score;
            this.saveHighScore();
            if (this.onHighScore) this.onHighScore(this.highScore);
        }

        if (this.onScoreChange) this.onScoreChange(this.score, finalPoints, reason);
        return finalPoints;
    }

    incrementCombo() {
        this.comboCount++;
        this.comboTimer = this.comboTimeout;

        // Update multiplier based on combo
        const newMultiplier = Math.min(
            this.maxMultiplier,
            1 + Math.floor(this.comboCount / 3)
        );

        if (newMultiplier !== this.multiplier) {
            this.multiplier = newMultiplier;
            if (this.onMultiplierChange) this.onMultiplierChange(this.multiplier);
        }

        if (this.onComboChange) this.onComboChange(this.comboCount, this.comboTimer);
    }

    resetCombo() {
        this.comboCount = 0;
        this.comboTimer = 0;
        this.multiplier = 1;
        if (this.onComboChange) this.onComboChange(0, 0);
        if (this.onMultiplierChange) this.onMultiplierChange(1);
    }

    getSessionTime() {
        return Math.floor((Date.now() - this.sessionStartTime) / 1000);
    }

    getStats() {
        return {
            score: this.score,
            highScore: this.highScore,
            combo: this.comboCount,
            multiplier: this.multiplier,
            coinsCollected: this.totalCoinsCollected,
            checkpoints: this.totalCheckpoints,
            challengesCompleted: this.totalChallengesCompleted,
            sessionTime: this.getSessionTime()
        };
    }

    loadHighScore() {
        try {
            return parseInt(localStorage.getItem('tankup_highscore') || '0', 10);
        } catch {
            return 0;
        }
    }

    saveHighScore() {
        try {
            localStorage.setItem('tankup_highscore', String(this.highScore));
        } catch {
            // Storage unavailable
        }
    }

    reset() {
        this.score = 0;
        this.comboCount = 0;
        this.comboTimer = 0;
        this.multiplier = 1;
        this.totalCoinsCollected = 0;
        this.totalCheckpoints = 0;
        this.totalChallengesCompleted = 0;
        this.speedBonusAccumulator = 0;
        this.sessionStartTime = Date.now();
    }
}
